import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { styled, useMediaQuery } from "@mui/system";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Slide,
  Fade,
  Tooltip,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsureAssistStyles from "./InsureAssistStyles";
import { RootState } from "../../../store/store";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  IGiveMessageFeedbackRequestParams,
  IMessage,
} from "../../../models/user/InsureAssist/InsureAssist";
import {
  cancelAudioRecording,
  startAudioRecording,
  stopAudioRecording,
} from "./AudioRecording";
import axios from "axios";
import {
  clearChat,
  giveMessageFeedback,
} from "../../../store/user/insureAssist/insureAssistActions";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ThreeDotsLoader from "../../../components/ThreeDotsLoader/ThreeDotsLoader";
import { Typed } from "react-typed";
import MinimizeIcon from "@mui/icons-material/Minimize";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import icon from "../../../assets/images/logo.png";
import { useTranslation } from "react-i18next";
import i18n from "../../../translator/i18next";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import InsureAssistFeedbackForm from "./InsureAssistFeedbackForm";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCookie } from "../../../utils/Utility";
import { ALL_AGENT_ROLE_CODES, state } from "../../../constants/Constants";
import CustomSkeleton from "../../../common/CustomSkeleton/CustomSkeleton";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { closeChat } from "../../../store/common/chatUISlice";
import MentionInput from "./MentionInput";

const ChatPopup = styled(Paper)(({ theme }) => ({
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  // Use a fixed width on mobile for consistent sizing
  width: 320,
  maxWidth: "unset",
  display: "flex",
  flexDirection: "column",
  // borderTopRightRadius: 0,
  // borderBottomRightRadius: 0,
  // borderTopLeftRadius: 16,
  // borderBottomLeftRadius: 16,
  overflow: "hidden",
  // boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  zIndex: 1400,
  // [theme.breakpoints.down("sm")]: {
  //   width: "100vw",
  //   left: 0,
  //   right: 0,
  // },
  [theme.breakpoints.up("sm")]: {
    width: 306,
  },
  [theme.breakpoints.up("md")]: {
    width: 346,
  },
  [theme.breakpoints.up("lg")]: {
    width: 420,
  },
}));

const ChatHeader = styled(Box)({
  padding: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#fcfcf8",
});

const ChatContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: "20px",
  // [theme.breakpoints.down("sm")]: {
  //   padding: "12px",
  // },
}));

const MessageInput = styled(Box)(({ theme }) => ({
  padding: "12px",
  display: "flex",
  alignItems: "center",
  width: "100%",
  // [theme.breakpoints.down("sm")]: {
  //   padding: "8px",
  // },
}));

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  typed: Typed | undefined;
  searchedCustomerId?: string;
  setChatWidth?: Dispatch<SetStateAction<number | null>>;
}

const ResponsiveChatPopup: React.FC<IProps> = ({
  isOpen,
  setIsOpen,
  setIsDisabled,
  typed,
  searchedCustomerId,
  setChatWidth,
}) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const location = useLocation();
  const wsUrl = process.env.REACT_APP_CHAT_WEB_SOCKET_URL;
  const transcriptUrl = process.env.REACT_APP_CHAT_TRANSCRIPT_URL;
  const isChatOpen = useAppSelector((s) => s.chatUI.isOpen);
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);
  const { isLoading, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const isMobile = useMediaQuery("(max-width:768px)");
  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [message, setMessage] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [transcribeLoading, setTranscribeLoading] = useState<boolean>(false);
  const [transcribeError, setTranscribeError] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [unsupportedBrowser, setUnsupportedBrowser] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<string>("00:00");
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [voiceSocket, setVoiceSocket] = useState<WebSocket | null>(null);
  const [isVoiceActive, setIsVoiceActive] = useState<boolean>(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false);
  const [explainBtnVisible, setExplainBtnVisible] = useState<boolean>(false);
  const [explainBtnPos, setExplainBtnPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [selectedExplainText, setSelectedExplainText] = useState<string>("");
  const [chatWidth, setChatWidthState] = useState<number | null>(null);
  const isResizingRef = useRef<boolean>(false);
  const chatWidthRef = useRef<number | null>(null);
  const voiceMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceMediaStreamRef = useRef<MediaStream | null>(null);
  const voiceAudioContextRef = useRef<AudioContext | null>(null);
  const voiceAudioQueueRef = useRef<AudioBuffer[]>([]);
  const voiceIsPlayingRef = useRef<boolean>(false);
  // Realtime JSON protocol (front.js style) helpers
  const realtimeAudioContextRef = useRef<AudioContext | null>(null);
  const realtimeAudioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const realtimeAudioStreamRef = useRef<MediaStream | null>(null);
  const currentAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const realtimeAudioQueueRef = useRef<Uint8Array[]>([]);
  const realtimeIsPlayingRef = useRef<boolean>(false);
  const responseMarkdownBufferRef = useRef<Map<string, string>>(new Map());
  const lastResponseIdRef = useRef<string | null>(null);
  const streamingMessageIndexRef = useRef<Map<string, number>>(new Map());
  const navigate = useNavigate();
  const { initResponse } = useAppSelector((state: RootState) => {
    return state.insureAssist;
  });
  const sessionId = getCookie("sessionId");
  const roleCode = getCookie("roleCode");
  const producerId = getCookie("id");

  const [convoId, setConvoId] = useState<string | null>(null);

  // Default width when opening/refeshing if no stored width is found
  const DEFAULT_CHAT_WIDTH = 420; // px

  const newMessageRef = useRef<HTMLDivElement | null>(null);
  const chatPanelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Initialize chat width (persist across navigations in the session)
  useEffect(() => {
    if (isOpen) {
      // Always start with a sensible default on open/refresh, clamped to viewport
      const minWidth = 260;
      const maxWidth = Math.floor(window.innerWidth * 0.8);
      const defaultWidth = Math.max(minWidth, Math.min(maxWidth, DEFAULT_CHAT_WIDTH));
      setChatWidthState(defaultWidth);
      chatWidthRef.current = defaultWidth;
      if (setChatWidth) {
        setChatWidth(defaultWidth);
      }
      sessionStorage.setItem("insureAssistChatWidth", String(defaultWidth));
    }
  }, [isOpen, setChatWidth]);

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    try {
      document.body.style.cursor = "col-resize";
    } catch (err) {}

    const onMove = (ev: MouseEvent) => {
      if (!isResizingRef.current) return;
      const minWidth = 260; // minimum chat width
      // Allow dragging up to 80% of the screen width
      const maxWidth = Math.floor(window.innerWidth * 0.8);
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, window.innerWidth - ev.clientX)
      );
      setChatWidthState(newWidth);
      chatWidthRef.current = newWidth;
      if (setChatWidth) {
        setChatWidth(newWidth);
      }
      // Main content spacing is managed by the layout container; no body padding here
    };

    const onUp = () => {
      isResizingRef.current = false;
      try {
        document.body.style.cursor = "";
      } catch (err) {}
      if (chatWidthRef.current) {
        sessionStorage.setItem(
          "insureAssistChatWidth",
          String(chatWidthRef.current)
        );
      }
      document.removeEventListener("mousemove", onMove as any);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove as any);
    document.addEventListener("mouseup", onUp);
  };

  // Dynamically load marked for markdown rendering from voice text payloads
  useEffect(() => {
    if (!(window as any).marked) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/marked@13.0.2/marked.min.js";
      script.async = true;
      document.body.appendChild(script);
      return () => {
        try {
          document.body.removeChild(script);
        } catch (e) {}
      };
    }
  }, []);

  const sendMessage = (arg?: React.FormEvent | string) => {
    if (arg && typeof arg !== "string") arg.preventDefault();
    const content = typeof arg === "string" ? arg : message;
    if (content.trim() !== "" && socket) {
      const messageObj: IMessage = {
        content,
        carrierPolicyNumber:
          location.pathname !== "/customer/dashboard"
            ? policyDetailsHeader?.carrierPolicyNumber
            : "ALL",
        creationTime: new Date().toString(),
        level:
          messages.length !== 0 ? +messages[messages.length - 1].level + 1 : 0,
        sender: "customer",
        type: "text",
        convoId: convoId || "",
      };
      socket.send(JSON.stringify(messageObj));
      setMessage("");
      setMessages((prevState) => [...prevState, messageObj]);
      setLoading(true);
    }
  };

  const handleServiceClick = (message: string) => {
    if (socket && message.trim() !== "") {
      const messageObj: IMessage = {
        content: message,
        creationTime: new Date().toString(),
        level: messages.length,
        sender: "customer",
        type: "text",
        convoId: convoId || "",
      };
      socket.send(JSON.stringify(messageObj));
      setMessage("");
      setMessages((prevState) => [...prevState, messageObj]);
      setLoading(true);
    }
  };

  const handleStartRecording = () => {
    startAudioRecording(
      (value) => setIsRecording(value),
      (value) => setUnsupportedBrowser(value),
      (value) => setElapsedTime(value)
    );
  };

  const transcribeAudio = async (audioMessage: IMessage) => {
    if (transcriptUrl) {
      try {
        setTranscribeLoading(true);
        setTranscribeError(false);
        const response = await axios.post(transcriptUrl, audioMessage, {
          withCredentials: true,
        });
        if (response.status !== 200) {
          throw new Error("Failed to transcribe audio");
        }
        const data = response.data;
        setTranscribeLoading(false);
        return data;
      } catch (error) {
        setTranscribeLoading(false);
        setTranscribeError(true);
        console.error("Error transcribing audio:", error);
        return null;
      }
    }
  };

  const handleStopRecording = () => {
    stopAudioRecording(async (blob) => {
      if (blob && socket) {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64data = reader.result;
          if (typeof base64data === "string") {
            let audioMessage: IMessage = {
              content: base64data,
              creationTime: new Date().toString(),
              level: messages.length,
              sender: "customer",
              type: "audio",
              convoId: convoId || "",
            };
            setMessages((prevState) => [...prevState, audioMessage]);
            setLoading(true);
            socket.send(JSON.stringify(audioMessage));
            audioMessage.transcription = await transcribeAudio(audioMessage);
          } else {
            console.error("Failed to read audio blob as a string.");
          }
        };
      }
      setIsRecording(false);
    });
    cancelAudioRecording();
  };

  const startVoiceStreaming = async (ws: WebSocket) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceMediaStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      voiceMediaRecorderRef.current = mediaRecorder;
      ws.binaryType = "arraybuffer";
      mediaRecorder.ondataavailable = async (event) => {
        if (
          event.data &&
          event.data.size > 0 &&
          ws.readyState === WebSocket.OPEN
        ) {
          try {
            const buffer = await event.data.arrayBuffer();
            ws.send(buffer);
          } catch (e) {
            // ignore chunk send errors
          }
        }
      };
      mediaRecorder.start(250);
    } catch (err) {
      console.error("Failed to start voice streaming", err);
      setIsVoiceActive(false);
    }
  };

  const stopVoiceStreaming = () => {
    try {
      if (
        voiceMediaRecorderRef.current &&
        voiceMediaRecorderRef.current.state !== "inactive"
      ) {
        voiceMediaRecorderRef.current.stop();
      }
    } catch (e) {}
    try {
      if (voiceMediaStreamRef.current) {
        voiceMediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {}
    voiceMediaRecorderRef.current = null;
    voiceMediaStreamRef.current = null;
  };

  // Realtime protocol utilities (adapted from front.js)
  const base64EncodeAudio = (uint8Array: Uint8Array) => {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
  };

  const floatTo16BitPCM = (float32Array: Float32Array) => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  };

  const concatenateBuffers = (buffer1: ArrayBuffer, buffer2: ArrayBuffer) => {
    const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
    tmp.set(new Uint8Array(buffer1), 0);
    tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
    return tmp.buffer;
  };

  const createWavBuffer = (pcmBuffer: ArrayBuffer, sampleRate: number) => {
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);
    const writeString = (v: DataView, offset: number, str: string) => {
      for (let i = 0; i < str.length; i++)
        v.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + pcmBuffer.byteLength, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, "data");
    view.setUint32(40, pcmBuffer.byteLength, true);
    return concatenateBuffers(wavHeader, pcmBuffer);
  };

  const stopCurrentAudioPlayback = () => {
    try {
      if (currentAudioSourceRef.current) {
        currentAudioSourceRef.current.stop();
        currentAudioSourceRef.current.disconnect();
      }
    } catch (e) {}
    currentAudioSourceRef.current = null;
  };

  const playPCM = (pcmBuffer: Uint8Array, callback: () => void) => {
    const ctx = realtimeAudioContextRef.current;
    if (!ctx) return callback();
    const wavBuffer: ArrayBuffer = createWavBuffer(
      new Uint8Array(pcmBuffer).buffer as ArrayBuffer,
      24000
    ) as ArrayBuffer;
    const arrBuf: ArrayBuffer = (wavBuffer as ArrayBuffer).slice(0);
    ctx.decodeAudioData(
      arrBuf,
      (audioBuffer) => {
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        source.onended = callback;
        try {
          source.start(0);
          currentAudioSourceRef.current = source;
        } catch (e) {
          callback();
        }
      },
      () => callback()
    );
  };

  const playNextAudio = () => {
    const queue = realtimeAudioQueueRef.current;
    if (queue.length > 0) {
      realtimeIsPlayingRef.current = true;
      const audioData = queue.shift()!;
      playPCM(audioData, playNextAudio);
    } else {
      realtimeIsPlayingRef.current = false;
    }
  };

  const stopRealtimeRecording = () => {
    try {
      if (realtimeAudioProcessorRef.current) {
        realtimeAudioProcessorRef.current.disconnect();
      }
    } catch (e) {}
    try {
      if (realtimeAudioStreamRef.current) {
        realtimeAudioStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {}
    stopCurrentAudioPlayback();
    realtimeAudioProcessorRef.current = null;
    realtimeAudioStreamRef.current = null;
  };

  const startRealtimeRecording = (wsParam?: WebSocket) => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const AudioCtx =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        realtimeAudioContextRef.current = new AudioCtx({ sampleRate: 24000 });
        realtimeAudioStreamRef.current = stream;
        const ctx = realtimeAudioContextRef.current!;
        const source = ctx.createMediaStreamSource(stream);
        const processor = ctx.createScriptProcessor(8192, 1, 1);
        realtimeAudioProcessorRef.current = processor;
        processor.onaudioprocess = (event) => {
          const wsToUse = wsParam || voiceSocket;
          if (wsToUse && wsToUse.readyState === WebSocket.OPEN) {
            const inputBuffer = event.inputBuffer.getChannelData(0);
            const pcm = new Uint8Array(floatTo16BitPCM(inputBuffer));
            const base64PCM = base64EncodeAudio(pcm);
            const chunkSize = 4096;
            for (let i = 0; i < base64PCM.length; i += chunkSize) {
              const chunk = base64PCM.slice(i, i + chunkSize);
              try {
                wsToUse.send(
                  JSON.stringify({
                    type: "input_audio_buffer.append",
                    audio: chunk,
                  })
                );
              } catch (e) {}
            }
          }
        };
        source.connect(processor);
        processor.connect(ctx.destination);
      })
      .catch(() => {
        setIsVoiceActive(false);
      });
  };

  const handleToggleVoiceConversation = () => {
   const realtimeWsUrl = process.env.REACT_APP_CHAT_REALTIME_VOICE_URL;
    if (!isVoiceActive && realtimeWsUrl ) {
      try {
        const ws = new WebSocket(realtimeWsUrl);
        ws.onopen = () => {
          setVoiceSocket(ws);
          setIsVoiceActive(true);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            switch (data.type) {
              case "session.created": {
                startRealtimeRecording(ws);
                break;
              }
              case "input_audio_buffer.speech_started": {
                stopCurrentAudioPlayback();
                realtimeAudioQueueRef.current = [];
                realtimeIsPlayingRef.current = false;
                setIsUserSpeaking(true);
                break;
              }
              case "input_audio_buffer.speech_stopped": {
                try {
                  ws.send(
                    JSON.stringify({ type: "input_audio_buffer.commit" })
                  );
                  ws.send(
                    JSON.stringify({
                      type: "response.create",
                      response: { modalities: ["text", "audio"] },
                    })
                  );
                } catch (e) {}
                setIsUserSpeaking(false);
                break;
              }
              case "conversation.item.input_audio_transcription.completed": {
                const userText: string = data.transcript;
                const messageObj: IMessage = {
                  content: userText,
                  creationTime: new Date().toString(),
                  level:
                    messages.length !== 0
                      ? +messages[messages.length - 1].level + 1
                      : 0,
                  sender: "customer",
                  type: "text",
                  convoId: convoId || "",
                };
                setMessages((prev) => [...prev, messageObj]);
                break;
              }
              case "response.output_audio_transcript.delta": {
                const transcript: string = data.delta || "";
                const responseId: string = data.response_id || "default";
                lastResponseIdRef.current = responseId;
                const buf = responseMarkdownBufferRef.current;
                const nextContent = (buf.get(responseId) || "") + transcript;
                buf.set(responseId, nextContent);

                const marked = (window as any).marked;
                const html = marked ? marked.parse(nextContent) : undefined;

                setMessages((prev) => {
                  const map = streamingMessageIndexRef.current;
                  let idx = map.get(responseId);
                  if (idx === undefined) {
                    const newMsg: IMessage = {
                      content: nextContent,
                      creationTime: new Date().toString(),
                      level:
                        prev.length !== 0
                          ? +prev[prev.length - 1].level + 1
                          : 0,
                      sender: "system",
                      type: "text",
                      convoId: convoId || "",
                    } as IMessage;
                    (newMsg as any).contentHtml = html;
                    (newMsg as any).responseId = responseId;
                    (newMsg as any).streaming = true;
                    const arr = [...prev, newMsg];
                    map.set(responseId, arr.length - 1);
                    return arr;
                  } else {
                    const arr = [...prev];
                    const current: any = { ...(arr[idx] as any) };
                    current.content = nextContent;
                    current.contentHtml = html;
                    arr[idx] = current as IMessage;
                    return arr;
                  }
                });
                break;
              }
              case "response.output_audio_transcript.done": {
                const responseId: string =
                  data.response_id || lastResponseIdRef.current || "default";
                streamingMessageIndexRef.current.delete(responseId);
                break;
              }
              case "response.output_audio.delta": {
                if (data.delta) {
                  const audioData = Uint8Array.from(atob(data.delta), (c) =>
                    c.charCodeAt(0)
                  );
                  realtimeAudioQueueRef.current.push(audioData);
                  if (!realtimeIsPlayingRef.current) {
                    playNextAudio();
                  }
                }
                break;
              }
              case "response.output_audio.done": {
                realtimeIsPlayingRef.current = false;
                break;
              }
              default:
                break;
            }
          } catch (e) {
            // ignore unrecognized payloads
          }
        };
        ws.onclose = () => {
          setIsVoiceActive(false);
          setIsUserSpeaking(false);
          setVoiceSocket(null);
          stopRealtimeRecording();
          try {
            if (
              realtimeAudioContextRef.current &&
              realtimeAudioContextRef.current.state !== "closed"
            ) {
              realtimeAudioContextRef.current.close();
            }
          } catch (e) {}
          realtimeAudioContextRef.current = null;
          realtimeAudioQueueRef.current = [];
          realtimeIsPlayingRef.current = false;
        };
        ws.onerror = () => {
          setIsVoiceActive(false);
          setIsUserSpeaking(false);
          setVoiceSocket(null);
          stopRealtimeRecording();
          try {
            if (
              realtimeAudioContextRef.current &&
              realtimeAudioContextRef.current.state !== "closed"
            ) {
              realtimeAudioContextRef.current.close();
            }
          } catch (e) {}
          realtimeAudioContextRef.current = null;
          realtimeAudioQueueRef.current = [];
          realtimeIsPlayingRef.current = false;
        };
      } catch (e) {
        console.error("Failed to open realtime voice socket", e);
      }
    } else {
      stopRealtimeRecording();
      if (voiceSocket) {
        try {
          voiceSocket.close();
        } catch (e) {}
      }
      setIsVoiceActive(false);
      setIsUserSpeaking(false);
      setVoiceSocket(null);
      try {
        if (
          realtimeAudioContextRef.current &&
          realtimeAudioContextRef.current.state !== "closed"
        ) {
          realtimeAudioContextRef.current.close();
        }
      } catch (e) {}
      realtimeAudioContextRef.current = null;
      realtimeAudioQueueRef.current = [];
      realtimeIsPlayingRef.current = false;
    }
  };

  const handleClose = useCallback(() => {
    if (
      (ALL_AGENT_ROLE_CODES.includes(roleCode) &&
        searchedCustomerId &&
        searchedCustomerId?.trim()) ||
      (roleCode === "2" && userDetails && userDetails.customerId)
    ) {
      dispatch(closeChat());
      handleStopRecording();
      if (voiceSocket) {
        try {
          voiceSocket.close();
        } catch (e) {
          // ...
        }
      }
      stopVoiceStreaming();
      // Ensure realtime voice is fully stopped as well
      stopRealtimeRecording();
      setIsVoiceActive(false);
      setIsUserSpeaking(false);
      setVoiceSocket(null);
      try {
        if (
          realtimeAudioContextRef.current &&
          realtimeAudioContextRef.current.state !== "closed"
        ) {
          realtimeAudioContextRef.current.close();
        }
      } catch (e) {}
      realtimeAudioContextRef.current = null;
      realtimeAudioQueueRef.current = [];
      realtimeIsPlayingRef.current = false;

      setMessages([]);
      setMessage("");
      setIsOpen(false);
      setLoading(false);
      setSocket(null);
      setUnsupportedBrowser(false);
      setElapsedTime("00:00");

      handleClearChat();
      setIsDisabled(false);
      setIsMinimized(false);
      typed?.start();

      sessionStorage.removeItem("insureAssistOpen");
      sessionStorage.removeItem("insureAssistMinimized");
      sessionStorage.removeItem("insureAssistPopupMessages");
      sessionStorage.removeItem("insureAssistPopupConvoId");
    }
  }, [userDetails, dispatch, setIsOpen, setIsDisabled, typed]);

  const handleClearChat = useCallback(() => {
    if (
      (ALL_AGENT_ROLE_CODES.includes(roleCode) &&
        searchedCustomerId &&
        searchedCustomerId?.trim()) ||
      (roleCode === "2" && userDetails && userDetails.customerId)
    ) {
      setConvoId(null);
      const requestParams: { userId: string; param: string } = {
        userId: ALL_AGENT_ROLE_CODES.includes(roleCode)
          ? `${searchedCustomerId}-${producerId}`
          : userDetails?.customerId || "",
        param: "qa",
      };

      dispatch(clearChat(requestParams));
      setMessages([]);
    }
  }, [userDetails, searchedCustomerId, roleCode, dispatch]);

  useEffect(() => {
    window.addEventListener("beforeunload", handleClose);
    return () => {
      window.removeEventListener("beforeunload", handleClose);
      if (voiceSocket) {
        try {
          voiceSocket.close();
        } catch (e) {
          // ...
        }
      }
      try {
        voiceAudioQueueRef.current = [];
        if (
          voiceAudioContextRef.current &&
          voiceAudioContextRef.current.state !== "closed"
        ) {
          voiceAudioContextRef.current.close();
        }
      } catch (e) {}
      voiceAudioContextRef.current = null;
      voiceIsPlayingRef.current = false;
    };
  }, []);

  const generateIAApiUrl = (roleCode: string, producerId?: string) => {
    if (roleCode === "2") {
      return `${wsUrl}?customerId=${userDetails?.customerId}&language=${i18n.language}&sessionId=${sessionId}&userType=Customer`;
    } else if (roleCode === "Producer" && producerId && producerId?.trim()) {
      return `${wsUrl}?customerId=${searchedCustomerId}&language=${i18n.language}&sessionId=${sessionId}&userType=Agent&producerId=${producerId}`;
    }
  };

  useEffect(() => {
    if (isOpen && wsUrl && roleCode) {
      const ws = new WebSocket(
        generateIAApiUrl(roleCode, producerId) as string
      );

      ws.onopen = () => {
        setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const messageObj: IMessage = {
              content: "ping",
              creationTime: new Date().toString(),
              level: 0,
              sender: "customer",
              type: "ping",
              convoId: "",
            };
            ws.send(JSON.stringify(messageObj));
          }
        }, 55000);

        if (!sessionStorage.getItem("insureAssistPopupMessages")) {
          setMessages([]);
        }
      };

      ws.onclose = () => {
        handleClose();
      };

      ws.onmessage = async (event) => {
        try {
          const serverMessage = JSON.parse(event.data);
          if (serverMessage.type === "SESSION_ESTABLISHED") {
            setConvoId(serverMessage.convoId);
          } else if (serverMessage.type === "pong") {
          } else {
            setConvoId(serverMessage.convoId);
            setMessages((prevMessages) => {
              const newMessages = [...prevMessages, serverMessage];
              sessionStorage.setItem(
                "insureAssistPopupMessages",
                JSON.stringify(newMessages)
              );
              return newMessages;
            });
            setLoading(false);
          }
        } catch (e) {
          // Non-JSON payload: treat as text (render with marked if available)
          if (typeof event.data === "string") {
            try {
              const marked = (window as any).marked;
              const html = marked ? marked.parse(event.data) : null;
              const textMessage: IMessage = {
                content: event.data,
                creationTime: new Date().toString(),
                level:
                  messages.length !== 0
                    ? +messages[messages.length - 1].level + 1
                    : 0,
                sender: "system",
                type: "text",
                convoId: convoId || "",
              };
              (textMessage as any).contentHtml = html || undefined;
              setMessages((prev) => [...prev, textMessage]);
              setLoading(false);
            } catch (err) {
              // swallow bad text payloads
            }
          }
        }
      };

      ws.onerror = (error) => {
        handleClose();
        console.error("WebSocket error:", error);
      };

      setSocket(ws);

      return () => {
        ws.close();
      };
    }
  }, [isOpen, wsUrl, userDetails, handleClose]);

  useEffect(() => {
    if (newMessageRef.current) {
      newMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages]);

  useEffect(() => {
    if (unsupportedBrowser) {
      if (
        (ALL_AGENT_ROLE_CODES.includes(roleCode) &&
          searchedCustomerId &&
          searchedCustomerId?.trim()) ||
        (roleCode === "2" && userDetails && userDetails.customerId)
      ) {
        const requestParams: { userId: string; param: string } = {
          userId: ALL_AGENT_ROLE_CODES.includes(roleCode)
            ? `${searchedCustomerId}-${producerId}`
            : userDetails?.customerId || "",
          param: "qa",
        };
        dispatch(clearChat(requestParams));
      }
      setIsOpen((prevState: boolean) => !prevState);
    }
  }, [unsupportedBrowser, setIsOpen, dispatch, userDetails]);

  const services = useMemo(() => {
     let titles: string[] = [];
   
    if (initResponse) {
      if (initResponse.includes("-> ")) {
        titles = initResponse.split("-> ").slice(1);
      } else if (initResponse.includes("->")) {
        titles = initResponse.split("->").slice(1);
      }
    }

    const icons = [
      <PolicyOutlinedIcon sx={{ fontSize: "16px" }} />,
      <AttachMoneyOutlinedIcon sx={{ fontSize: "16px" }} />,
      <InfoOutlinedIcon sx={{ fontSize: "16px" }} />,
      <AutoStoriesOutlinedIcon sx={{ fontSize: "16px" }} />,
    ];

    return titles.map((title, index) => ({
      title,
      icon: icons[index % icons.length],
      description: "",
    }));
  }, [initResponse]);

  const [openFeedbackPopup, setOpenFeedbackPopup] = useState(false);
  const [feedbackDescription, setFeedbackDescription] = useState("");

  const handleFeedback = (message: IMessage, feedback: string) => {
    if (feedback === "P") {
      const newFeedback = message.feedback === "P" ? "" : "P";
      const updatedMessage = { ...message, feedback: newFeedback };
      const index = messages.findIndex((msg) => msg === message);
      setMessages((prevState) => {
        if (index !== -1) {
          prevState[index] = updatedMessage;
        }
        return [...prevState];
      });

      const requestParams: IGiveMessageFeedbackRequestParams = {
        sessionId: sessionId,
        level: message.level,
        feedback: newFeedback,
        comments: feedbackDescription,
        convoId: convoId || "",
      };
      dispatch(giveMessageFeedback(requestParams));
    } else if (feedback === "N") {
      setCurrentIndex(messages.findIndex((msg) => msg === message));
      setOpenFeedbackPopup(true);
    }
  };

  const handleFeedbackSubmit = (currentMsg: IMessage) => {
    const updatedMessage = { ...currentMsg, feedback: "N" };
    const index = messages.findIndex((msg) => msg === currentMsg);
    setMessages((prevState) => {
      if (index !== -1) {
        prevState[index] = updatedMessage;
      }
      return [...prevState];
    });

    const requestParams: IGiveMessageFeedbackRequestParams = {
      sessionId: sessionId,
      level: currentMsg.level,
      feedback: "N",
      comments: feedbackDescription,
      convoId: convoId || "",
    };
    dispatch(giveMessageFeedback(requestParams));
    setOpenFeedbackPopup(false);
    setFeedbackDescription("");
  };

  const isInternalLink = (href: string): boolean => {
    const baseUrl = window.location.origin;
    try {
      const url = new URL(href);
      return url.origin === baseUrl;
    } catch {
      return false;
    }
  };

  const handleLinkClick = (href: string, event: React.MouseEvent) => {
    if (isInternalLink(href)) {
      event.preventDefault();
      //setIsMinimized(true);
      sessionStorage.setItem("insureAssistOpen", "true");
      sessionStorage.setItem("insureAssistMinimized", "true");
      sessionStorage.setItem("insureAssistPopupMessages", JSON.stringify(messages));
      sessionStorage.setItem("insureAssistPopupConvoId", convoId || "");

      const relativePath = href.replace(window.location.origin, "");
      navigate(relativePath);
    }
  };

  // Show floating Explain button when user selects text outside the chat panel (left side)
  useEffect(() => {
    const handleSelectionChange = () => {
      try {
        const selection = window.getSelection();
        const selectedText = selection ? selection.toString().trim() : "";
        
        // Always hide the button if no text is selected
        if (!selectedText) {
          setExplainBtnVisible(false);
          return;
        }

        const anchorNode = selection?.anchorNode || null;
        const chatEl = chatPanelRef.current;
        if (anchorNode && chatEl && chatEl.contains(anchorNode)) {
          setExplainBtnVisible(false);
          return;
        }

        // Position above the selected range
        const range = selection?.rangeCount ? selection!.getRangeAt(0) : null;
        const rect = range ? range.getBoundingClientRect() : null;
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          setExplainBtnVisible(false);
          return;
        }

        // Ensure selection is to the left of the chat panel
        const chatRect = chatEl ? chatEl.getBoundingClientRect() : null;
        if (chatRect && rect.right >= chatRect.left - 8) {
          setExplainBtnVisible(false);
          return;
        }

        const x = Math.min(Math.max(rect.left, 8), window.innerWidth - 100);
        const y = Math.max(rect.top - 36, 8);
        setExplainBtnPos({ x, y });
        setSelectedExplainText(selectedText);
        setExplainBtnVisible(true);
      } catch (e) {
        setExplainBtnVisible(false);
      }
    };

    const hideExplain = () => {
      setExplainBtnVisible(false);
    };

    // Handle scroll with position update
    const handleScroll = () => {
      if (explainBtnVisible) {
        // Update button position when scrolling
        const selection = window.getSelection();
        const selectedText = selection ? selection.toString().trim() : "";
        
        if (!selectedText) {
          setExplainBtnVisible(false);
          return;
        }

        const range = selection?.rangeCount ? selection!.getRangeAt(0) : null;
        const rect = range ? range.getBoundingClientRect() : null;
        
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          setExplainBtnVisible(false);
          return;
        }

        // Check if selection is still outside chat panel
        const chatEl = chatPanelRef.current;
        const chatRect = chatEl ? chatEl.getBoundingClientRect() : null;
        if (chatRect && rect.right >= chatRect.left - 8) {
          setExplainBtnVisible(false);
          return;
        }

        // Update position
        const x = Math.min(Math.max(rect.left, 8), window.innerWidth - 100);
        const y = Math.max(rect.top - 36, 8);
        setExplainBtnPos({ x, y });
      }
    };

    // Handle selection changes
    const handleSelectionStart = () => {
      // Small delay to ensure selection is properly established
      setTimeout(handleSelectionChange, 10);
    };

    // Handle clicks that might clear selection
    const handleClick = (e: MouseEvent) => {
      // Check if clicking outside the explain button
      const explainButton = document.querySelector('[data-explain-button]');
      if (explainButton && explainButton.contains(e.target as Node)) {
        return; // Don't hide if clicking the explain button itself
      }
      
      // Small delay to check if selection was cleared
      setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection ? selection.toString().trim() : "";
        if (!selectedText) {
          setExplainBtnVisible(false);
        }
      }, 10);
    };

    // Add event listeners
    document.addEventListener("mouseup", handleSelectionStart);
    document.addEventListener("keyup", handleSelectionStart);
    document.addEventListener("click", handleClick);
    document.addEventListener("selectionchange", handleSelectionChange);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", hideExplain);

    return () => {
      document.removeEventListener("mouseup", handleSelectionStart);
      document.removeEventListener("keyup", handleSelectionStart);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("selectionchange", handleSelectionChange);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", hideExplain);
    };
  }, [explainBtnVisible]);

  const handleExplainSelectedText = () => {
    const text = selectedExplainText?.trim();
    if (text) {
      if (socket) {
        sendMessage(`Explain ${text}`);
      } else {
        setMessage(`Explain ${text}`);
        try {
          inputRef.current?.focus();
        } catch (e) {}
      }
      setExplainBtnVisible(false);
      try {
        const sel = window.getSelection();
        sel && sel.removeAllRanges();
      } catch (e) {}
    }
  };

  useEffect(() => {
    const storedOpen = sessionStorage.getItem("insureAssistOpen");
    const storedMinimized = sessionStorage.getItem("insureAssistMinimized");
    const storedMessages = sessionStorage.getItem("insureAssistPopupMessages");
    const storedConvoId = sessionStorage.getItem("insureAssistPopupConvoId");

    if (storedOpen === "true") {
      setIsOpen(true);
    }
    if (storedMinimized === "true") {
      setIsMinimized(true);
    }
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }
    if (storedConvoId) {
      setConvoId(storedConvoId);
    }
  }, [setIsOpen]);

  return (
    <>
      {explainBtnVisible && isChatOpen && (
        <Box
          sx={{
            position: "fixed",
            left: `${explainBtnPos.x}px`,
            top: `${explainBtnPos.y}px`,
            zIndex: 1400,
          }}
        >
          <Paper elevation={3} sx={{ borderRadius: "16px" }}>
            <Tooltip title={t("Explain selected text in chat")}>
              <Button
                data-explain-button
                variant="outlined"
                size="small"
                startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={handleExplainSelectedText}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                {t("Explain")}
              </Button>
            </Tooltip>
          </Paper>
        </Box>
      )}
      <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
        <ChatPopup
          id="chat-panel"
          ref={chatPanelRef as any}
          sx={{
            height: isMinimized ? "50px" : "100vh",
            width: !isMobile && chatWidth ? `${chatWidth}px` : undefined,
            flex: "0 0 auto",
          }}
          style={{ width: !isMobile && chatWidth ? `${chatWidth}px` : undefined }}
        >
          {/* Left resize handle */}
          <Box
            onMouseDown={handleResizeMouseDown}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 10,
              height: "100%",
              cursor: "col-resize",
              zIndex: 1,
              // Optional visual cue on hover
              '&:hover': {
                background: 'rgba(0,0,0,0.02)'
              }
            }}
          />
          <ChatHeader
            sx={{
              borderBottom: "1px solid #ddd",
              borderBottomColor: uiSettings?.iconColor,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  padding: "5px",
                  height: "35px",
                  width: "35px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={icon}
                  alt=""
                  style={{ height: "100%", width: "100%" }}
                />
              </Box>
              <Typography>{t("InsureAssist")}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* <IconButton size="small" title="Open in full screen">
                <OpenInFullIcon sx={{ fontSize: "20px" }} />
              </IconButton> */}
              {/* <IconButton
                size="small"
                title={isMinimized ? t("Maximize") : t("Minimize")}
                onClick={() => setIsMinimized((prevState) => !prevState)}
              >
                {isMinimized ? (
                  <CropSquareIcon sx={{ fontSize: "20px" }} />
                ) : (
                  <MinimizeIcon sx={{ fontSize: "20px" }} />
                )}
              </IconButton> */}
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label="Close chat"
                title={t("Close")}
              >
                <CloseIcon sx={{ fontSize: "20px" }} />
              </IconButton>
            </Box>
          </ChatHeader>
          <ChatContent sx={{ display: isMinimized ? "none" : "block" }}>
            <Fade in={true}>
              <Box
                sx={{
                  ...InsureAssistStyles.messagesContainer,
                  overflowY: messages.length > 0 ? "auto" : "hidden",
                }}
              >
                {messages.length > 0 ? (
                  messages.map((message, index) =>
                    message.content ? (
                      <Box
                        key={`${message.creationTime}_${index}`}
                        ref={
                          index === messages.length - 1 ? newMessageRef : null
                        }
                        sx={{
                          ...InsureAssistStyles.messageBox,
                          justifyContent:
                            message.sender === "customer"
                              ? "flex-end"
                              : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            ...InsureAssistStyles.messageContent,
                            backgroundColor:
                              message.type === "audio"
                                ? "#ffffff"
                                : "rgba(55, 53, 47, 0.04)",
                            minWidth: message.type === "audio" ? "80%" : "20%",
                          }}
                        >
                          <Box
                            sx={{
                              ...InsureAssistStyles.messageContentBox,
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {message.type === "audio" ? (
                              <>
                                <audio
                                  controls
                                  src={message.content as string}
                                  style={{ width: "100%" }}
                                ></audio>
                                {(transcribeLoading &&
                                  message.level === messages.length - 1) ||
                                message.level === messages.length ? (
                                  <Box sx={InsureAssistStyles.loadingBox}>
                                    <Skeleton animation="wave" />
                                  </Box>
                                ) : (
                                  <></>
                                )}
                                {transcribeError ? (
                                  <Typography sx={{ color: "red" }}>
                                    {t("Error transcribing audio")}
                                  </Typography>
                                ) : (
                                  <></>
                                )}
                                {message.transcription && (
                                  <Typography
                                    className="messageStyles"
                                    sx={{
                                      fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                                    }}
                                  >
                                    {message.transcription}
                                  </Typography>
                                )}
                              </>
                            ) : (message as any).contentHtml ? (
                              <div
                                className="messageStyles"
                                dangerouslySetInnerHTML={{
                                  __html: (message as any).contentHtml,
                                }}
                              />
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                components={{
                                  table: ({ node, ...props }) => (
                                    <table
                                      style={{
                                        borderCollapse: "collapse",
                                        width: "100%",
                                        overflow: "scroll",
                                      }}
                                      {...props}
                                    />
                                  ),
                                  th: ({ node, ...props }) => (
                                    <th
                                      style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        backgroundColor: "#f2f2f2",
                                        textAlign: "left",
                                      }}
                                      {...props}
                                    />
                                  ),
                                  td: ({ node, ...props }) => (
                                    <td
                                      style={{
                                        minWidth: "150px",
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                      }}
                                      {...props}
                                    />
                                  ),
                                  a: ({ node, children, href, ...props }) => (
                                    <a
                                      href={href}
                                      onClick={(e) =>
                                        handleLinkClick(href || "", e)
                                      }
                                      {...props}
                                    >
                                      {children}
                                    </a>
                                  ),
                                }}
                                className="messageStyles"
                              >
                                {message.content}
                              </ReactMarkdown>
                            )}
                          </Box>
                          {roleCode === "2" && message.sender !== "customer" ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  border: "1px solid #ddd",
                                  borderRadius: "15px",
                                  padding: "5px",
                                  width: "20%",
                                  gap: "15px",
                                }}
                              >
                                <ThumbUpAltOutlinedIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                    color:
                                      message.feedback === "P"
                                        ? "green"
                                        : "black",
                                  }}
                                  onClick={() => handleFeedback(message, "P")}
                                />
                                <ThumbDownAltOutlinedIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                    color:
                                      message.feedback === "N"
                                        ? "red"
                                        : "black",
                                  }}
                                  onClick={() => handleFeedback(message, "N")}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <></>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <></>
                    )
                  )
                ) : (
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                      }}
                    >
                      {t("Hi")} {userDetails?.customerName}
                      {t("! How can I help you today?")}
                    </Typography>
                    {roleCode === "2" && (
                      <Box
                        sx={{
                          marginTop: "10px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            padding: "8px",
                            fontSize: "12px",
                            color: "rgba(55, 53, 47, 0.65)",
                            fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                          }}
                        >
                          {t("Suggested")}
                        </Typography>
                        {isLoading &&
                        calledReducerType === "dashboard/getInitBasePrompt" ? (
                          <CustomSkeleton
                            count={4}
                            direction="column"
                            height="40px"
                            width="100%"
                            variant="rounded"
                          />
                        ) : (
                          services?.map((service, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px",
                                ":hover": {
                                  background: "rgba(53, 53, 47, 0.06)",
                                  borderRadius: "50px",
                                  cursor: "pointer",
                                  border: uiSettings?.iconColor
                                    ? `0.5px solid ${uiSettings?.iconColor}`
                                    : "none",
                                },
                              }}
                              onClick={() => handleServiceClick(service.title)}
                            >
                              {service.icon}
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                                }}
                              >
                                {service.title}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                {openFeedbackPopup && (
                  <InsureAssistFeedbackForm
                    openFeedbackPopup={openFeedbackPopup}
                    setOpenFeedbackPopup={setOpenFeedbackPopup}
                    feedbackDescription={feedbackDescription}
                    handleFeedbackSubmit={handleFeedbackSubmit}
                    setFeedbackDescription={setFeedbackDescription}
                    currentMsg={messages[currentIndex]}
                  />
                )}

                {loading ? (
                  <Box sx={{ display: "flex" }}>
                    <Typography
                      sx={{
                        fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                      }}
                    >
                      {t("Fetching details")}
                    </Typography>
                    <ThreeDotsLoader />
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            </Fade>
          </ChatContent>
          <MessageInput sx={{ display: isMinimized ? "none" : "flex" }}>
            <IconButton title={t("Clear Chat")} onClick={handleClearChat}>
              <DeleteSweepOutlinedIcon />
            </IconButton>
            <form onSubmit={sendMessage} style={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #cccccc",
                  borderRadius: "50px",
                  position: "relative",
                }}
              >
                <MentionInput
                  value={message}
                  onChange={setMessage}
                  placeholder={isRecording ? elapsedTime : t("Ask Anything or Select")}
                  disabled={isRecording}
                  inputRef={inputRef}
                  onSendMessage={sendMessage}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                  }}
                >
                  <Tooltip
                    title={
                      isRecording ? t("Click to Stop") : t("Click to Speak")
                    }
                  >
                    <IconButton
                      size="small"
                      sx={{
                        ...InsureAssistStyles.micIconButton,
                        border: isRecording ? "1px solid #ff0000" : "none",
                        animation: isRecording
                          ? "blink-animation 1.5s infinite"
                          : "none",
                      }}
                      edge="end"
                      onClick={
                        isRecording ? handleStopRecording : handleStartRecording
                      }
                      disabled={loading}
                    >
                      <MicIcon sx={InsureAssistStyles.sendIconButton} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" } }}
                    title={
                      isVoiceActive
                        ? t("Stop Voice Conversation")
                        : t("Start Voice Conversation")
                    }
                    onClick={handleToggleVoiceConversation}
                  >
                    <GraphicEqIcon
                      sx={{
                        fontSize: 18,
                        color: isVoiceActive
                          ? uiSettings?.iconColor || "#2563eb"
                          : undefined,
                        animation: isUserSpeaking
                          ? "equalize 1s infinite ease-in-out"
                          : undefined,
                        transformOrigin: "50% 50%",
                        "@keyframes equalize": {
                          "0%": { transform: "scaleY(0.7)" },
                          "25%": { transform: "scaleY(1.1)" },
                          "50%": { transform: "scaleY(0.85)" },
                          "75%": { transform: "scaleY(1.2)" },
                          "100%": { transform: "scaleY(0.7)" },
                        },
                      }}
                    />
                  </IconButton>
                  <Tooltip title={t("Send")}>
                    <IconButton
                      size="small"
                      type="submit"
                      disabled={isRecording || loading}
                      onClick={sendMessage}
                    >
                      <SendIcon sx={InsureAssistStyles.sendIconButton} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </form>
          </MessageInput>
          <Box sx={{ p: 2, pt: 0, display: "flex", alignItems: "center" }}>
            <Typography variant="body2" alignSelf={"center"}>
              {t("Any action or information here is subjected to this")}&nbsp;{" "}
            </Typography>
            <Link to={""} style={{ color: "skyblue" }}>
              {t("Terms & Conditions.")}
            </Link>
          </Box>
        </ChatPopup>
      </Slide>
    </>
  );
};

export default ResponsiveChatPopup;
